/**
 * Layers store — raster revision.
 *
 * Reference (bottom) · up to 5 raster Sketch layers · Trace (top, vector).
 *
 * Each sketch layer is a 512×512 canvas covering tile-space [0,1]². Strokes
 * are painted in (with symmetry duplication at the edges, see useDrawing) and
 * rendering is a single drawImage per tile placement — fast and smooth.
 * Undo/redo for sketch layers is snapshot-based (bounded). Trace layers are a
 * vector command stack; each Trace layer is one editable closed shape.
 *
 * Sketch layer backing store (count buffer revision):
 *   Each sketch layer has a Float32Array of TILE_RASTER² touch-counts alongside
 *   its HTMLCanvasElement. The canvas is a *derived* render — it is rebuilt from
 *   the counts at commit time and on theme change. Undo snapshots clone the
 *   Float32Array, not the canvas, so colour-scheme changes are free.
 */
import { applyLinear } from '@/composables/inkSnap';
import { apply, compose, IDENTITY, invert, isOrientationReversing, type Mat } from '@/geometry/affine';
import { buildTraceD } from '@/geometry/trace';
import { nearestIndexForHex, paletteHex } from '@/patterns/wallpaper';
import { tintCanvas as tintByAlpha } from '@/services/rasterTint';
import { renderWallpaperTiles } from '@/services/tileRenderer';
import type {
  InkPath,
  ReferenceState,
  SketchLayerData,
  SketchStroke,
  TraceLayerData,
  TraceLink,
  TraceNode,
} from '@/types/drawing';
import { defineStore } from 'pinia';
import { computed, markRaw, reactive, ref } from 'vue';

export type ActiveLayer = 'reference' | 'sketch' | 'ink' | 'trace';

/**
 * Raster layers come in two kinds that are NEVER mixed on one canvas (brief
 * Bug 4). They are stored, rendered and undone differently:
 *  - 'sketch' — soft graphite/glow pencil. Backed by a touch-count buffer
 *    (countRegistry); the canvas is a derived theme-coloured render. Never
 *    colour-tiled. Erased by zeroing counts.
 *  - 'ink' — opaque colour line. Painted directly onto the layer canvas, no
 *    count buffer at all. Colour-tiled per placement. Erased by destination-out
 *    on the canvas. This is also the kind PSD-imported artwork loads as.
 * Keeping them separate is what makes "sketch ignores colour", "ink colours per
 * transform", and "drawing sketch no longer wipes ink" all fall out cleanly —
 * the count-buffer re-render can't touch an ink canvas because ink has no counts.
 */
export type RasterLayerKind = 'sketch' | 'ink';

export const MAX_SKETCH_LAYERS = 5;
export const MAX_INK_LAYERS = 5;
/** up to ~5 interlocking shapes, one per Trace layer (brief §2) */
export const MAX_TRACE_LAYERS = 5;
/** raster resolution of one tile in pixels */
export const TILE_RASTER = 512;
/** max raster undo snapshots kept in memory (each ≈1MB for Float32Array) */
const RASTER_UNDO_DEPTH = 20;
const TRACE_UNDO_DEPTH = 100;

export interface SketchLayerMeta {
  id: string;
  name: string;
  opacity: number;
  visible: boolean;
  kind: RasterLayerKind;
}

// ---------------------------------------------------------------------------
// Count-buffer undo command — stores Float32Array snapshots, not canvases.
// The delete-layer command still carries the canvas (for the registry) AND the
// counts so both are restored together on undo.
// ---------------------------------------------------------------------------
type RasterCommand =
  | { kind: 'paint'; layerId: string; before: Float32Array; after: Float32Array | null }
  // Direct-canvas edit (imported-ink bucket fill): content lives on the layer
  // canvas, not in the count buffer, so its undo snapshots the canvas.
  | { kind: 'paint-canvas'; layerId: string; before: HTMLCanvasElement; after: HTMLCanvasElement | null }
  // Authored-ink edit (stroke / fill / erase): content lives across per-group
  // per-origin-class rasters, so its undo snapshots the whole group list.
  | { kind: 'paint-ink'; layerId: string; before: InkGroup[]; after: InkGroup[] | null }
  // Coloured-sketch edit (before real colour is active): content is spread
  // across per-origin-class COUNT buffers (colour travels with the stroke —
  // §), so undo snapshots the whole set (keyed by count id: base id = class
  // 0, `id#k` = class k).
  | { kind: 'paint-sketch'; layerId: string; before: Map<string, Float32Array>; after: Map<string, Float32Array> | null }
  // Coloured-sketch edit once real colour is active (brief 10-colours):
  // content lives across per-group per-origin-class count buffers, frozen
  // like authored ink, so undo snapshots the whole group list.
  | { kind: 'paint-sketch-groups'; layerId: string; before: SketchGroup[]; after: SketchGroup[] | null }
  // Clear-layer edit (Layers panel per-layer erase button): wipes all content
  // but keeps the layer meta, so its undo snapshots the whole content the same
  // way delete-layer does — the difference is the layer is never removed.
  | { kind: 'clear-raster'; layerId: string; before: LayerSnapshot; after: LayerSnapshot | null }
  | { kind: 'delete-layer'; layer: SketchLayerMeta; canvas: HTMLCanvasElement; counts: Float32Array; sketchClasses?: Map<number, Float32Array>; sketchGroups?: SketchGroup[]; inkGroups?: InkGroup[]; index: number };
// `layer.kind` on the delete-layer command decides which array (sketch or ink)
// it is re-inserted into, and whether its count buffer / ink class rasters are
// restored.

type TraceCommand =
  | { type: 'add'; layer: TraceLayerData }
  | { type: 'clear'; before: TraceLayerData; after: TraceLayerData }
  | { type: 'remove'; layer: TraceLayerData; index: number };

/**
 * Full non-destructive content capture of one raster layer — the same fields
 * `removeRasterLayer` snapshots before deleting a single layer, reused here so
 * "clear everything" can capture every layer the same way before wiping them.
 */
interface LayerSnapshot {
  layer: SketchLayerMeta;
  canvas: HTMLCanvasElement;
  counts: Float32Array;
  sketchClasses?: Map<number, Float32Array>;
  sketchGroups?: SketchGroup[];
  inkGroups?: InkGroup[];
}

/** Everything "clear everything" wipes, captured for one level of undo/redo. */
interface ClearAllSnapshot {
  sketchLayers: LayerSnapshot[];
  activeSketchLayerId: string;
  inkLayers: LayerSnapshot[];
  activeInkLayerId: string | null;
  traceLayers: TraceLayerData[];
  editingTraceLayerId: string | null;
}

/** Deep-copy a Trace layer (used for undo snapshots — nodes are plain data). */
function cloneTraceLayer(l: TraceLayerData): TraceLayerData {
  return {
    ...l,
    nodes: l.nodes.map((n) => ({
      ...n,
      inLink: n.inLink ? { ...n.inLink } : undefined,
      outLink: n.outLink ? { ...n.outLink } : undefined,
    })),
  };
}

/**
 * Should a linked node's in/out handle roles swap relative to its source?
 *
 * A reflection in the link transform *may* reverse the node's local edge order,
 * but not always — it depends on how the linked shape was actually drawn. So we
 * decide geometrically: transform the source's two neighbours through the link
 * and see whether they line up with the linked node's own prev/next (no swap) or
 * are flipped onto each other (swap). This is correct under rotation, reflection
 * and glide alike; the determinant sign is only a fallback for degenerate shapes.
 */
export function linkHandlesSwap(
  rootNodes: readonly TraceNode[],
  root: TraceNode,
  nodeNodes: readonly TraceNode[],
  node: TraceNode,
  m: Mat,
): boolean {
  const rn = rootNodes.length;
  const nn = nodeNodes.length;
  if (rn < 3 || nn < 3) return isOrientationReversing(m);
  const ri = rootNodes.indexOf(root);
  const ni = nodeNodes.indexOf(node);
  if (ri < 0 || ni < 0) return isOrientationReversing(m);
  const rPrev = rootNodes[(ri - 1 + rn) % rn]!;
  const rNext = rootNodes[(ri + 1) % rn]!;
  const nPrev = nodeNodes[(ni - 1 + nn) % nn]!;
  const nNext = nodeNodes[(ni + 1) % nn]!;
  const mp = apply(m, [rPrev.x, rPrev.y]);
  const mq = apply(m, [rNext.x, rNext.y]);
  const aligned = Math.hypot(mp[0] - nPrev.x, mp[1] - nPrev.y) + Math.hypot(mq[0] - nNext.x, mq[1] - nNext.y);
  const flipped = Math.hypot(mp[0] - nNext.x, mp[1] - nNext.y) + Math.hypot(mq[0] - nPrev.x, mq[1] - nPrev.y);
  return flipped < aligned;
}

/**
 * Position propagation is a graph walk, not a single hop (brief §3). A corner
 * node's two handle links can point at two different roots, so the corner plus
 * those roots plus every node linked (in either slot) to any of them form one
 * connected component. Dragging any node in that web moves the whole web —
 * every constraint being an exact isometry, the web stays consistent.
 *
 * Each link on a node A gives a constraint `A.pos = apply(link.matrix, S.pos)`,
 * usable in either direction. From the start node (`M = IDENTITY`), traversing
 * `S → A` composes `M_A = compose(matrix, M_S)`; traversing `A → S` composes
 * `M_S = compose(invert(matrix), M_A)`. Returns every reachable node with the
 * matrix that maps the drag's desired position onto that node's new position.
 *
 * Pure (no Pinia) so it can be unit-tested directly. A `visited` set (first
 * visit wins) guards re-visits and terminates any accidental cycle regardless
 * of whether the geometry is truly consistent.
 */
export function positionWeb(
  layers: readonly TraceLayerData[],
  startLayerId: string,
  startNodeId: string,
): { layerId: string; nodeId: string; m: Mat }[] {
  const key = (l: string, n: string): string => `${l}|${n}`;
  type Edge = { toLayer: string; toNode: string; m: Mat };
  const adj = new Map<string, Edge[]>();
  const addEdge = (fromL: string, fromN: string, toL: string, toN: string, m: Mat): void => {
    const k = key(fromL, fromN);
    const list = adj.get(k);
    if (list) list.push({ toLayer: toL, toNode: toN, m });
    else adj.set(k, [{ toLayer: toL, toNode: toN, m }]);
  };
  for (const layer of layers) {
    for (const node of layer.nodes) {
      for (const link of [node.inLink, node.outLink]) {
        if (!link) continue;
        // constraint: node.pos = apply(link.matrix, source.pos)
        addEdge(link.sourceLayerId, link.sourceNodeId, layer.id, node.id, link.matrix); // S → A
        addEdge(layer.id, node.id, link.sourceLayerId, link.sourceNodeId, invert(link.matrix)); // A → S
      }
    }
  }
  const result: { layerId: string; nodeId: string; m: Mat }[] = [];
  const visited = new Set<string>([key(startLayerId, startNodeId)]);
  const queue: { layerId: string; nodeId: string; m: Mat }[] = [
    { layerId: startLayerId, nodeId: startNodeId, m: IDENTITY },
  ];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    result.push(cur);
    const edges = adj.get(key(cur.layerId, cur.nodeId));
    if (!edges) continue;
    for (const e of edges) {
      const nk = key(e.toLayer, e.toNode);
      if (visited.has(nk)) continue;
      visited.add(nk);
      queue.push({ layerId: e.toLayer, nodeId: e.toNode, m: compose(e.m, cur.m) });
    }
  }
  return result;
}

/**
 * Migrate legacy whole-node `link`s (pre per-handle-linking) to the two-field
 * `inLink`/`outLink` model. The old model drove both handles + position from one
 * source through one matrix, deciding the in/out swap live via `linkHandlesSwap`.
 * We compute that swap once now (positions are unchanged by migration, so it is
 * exactly as accurate as it was at runtime) and persist it into both handle
 * links, which point at the same source — reproducing the old behaviour exactly.
 * Nodes without a legacy `link` pass through untouched. Presence-based (there is
 * no schema-version field in the document), matching this repo's convention.
 */
export function migrateTraceLayers(layers: TraceLayerData[]): TraceLayerData[] {
  const hasLegacyLink = layers.some((l) => l.nodes.some((n) => (n as TraceNode & { link?: TraceLink }).link));
  if (!hasLegacyLink) return layers;
  for (const layer of layers) {
    for (const node of layer.nodes) {
      const legacy = (node as TraceNode & { link?: TraceLink }).link;
      if (!legacy) continue;
      const sourceLayer = layers.find((l) => l.id === legacy.sourceLayerId);
      const source = sourceLayer?.nodes.find((n) => n.id === legacy.sourceNodeId);
      const swap =
        sourceLayer && source
          ? linkHandlesSwap(sourceLayer.nodes, source, layer.nodes, node, legacy.matrix)
          : isOrientationReversing(legacy.matrix);
      node.inLink = {
        sourceLayerId: legacy.sourceLayerId,
        sourceNodeId: legacy.sourceNodeId,
        sourceSlot: swap ? 'out' : 'in',
        matrix: legacy.matrix,
      };
      node.outLink = {
        sourceLayerId: legacy.sourceLayerId,
        sourceNodeId: legacy.sourceNodeId,
        sourceSlot: swap ? 'in' : 'out',
        matrix: legacy.matrix,
      };
      delete (node as TraceNode & { link?: TraceLink }).link;
    }
  }
  return layers;
}

/** Migrate a legacy flattened ink path to a (display-only) Trace layer. */
function inkPathToTraceLayer(p: InkPath, index: number): TraceLayerData {
  return {
    id: p.id,
    name: `Trace ${index + 1}`,
    opacity: 1,
    visible: true,
    nodes: [], // legacy paths have no structured nodes — render-only until redrawn
    closed: p.closed,
    d: p.d,
    stroke: p.stroke,
    strokeWidth: p.strokeWidth,
    timestamp: p.timestamp,
  };
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function makeSketchLayer(n: number, kind: RasterLayerKind = 'sketch'): SketchLayerMeta {
  const label = kind === 'ink' ? 'Ink' : 'Sketch';
  return { id: newId(), name: `${label} ${n}`, opacity: 1, visible: true, kind };
}

function defaultReference(): ReferenceState {
  return {
    imageDataUrl: null,
    transform: { tx: 0, ty: 0, scale: 1, rotation: 0 },
    opacity: 0.2,
    visible: true,
  };
}

// ---------------------------------------------------------------------------
// Registries — live outside reactivity (bitmaps / typed arrays, not state).
// ---------------------------------------------------------------------------

/** Render canvases — derived from countRegistry, used for drawImage. */
const canvasRegistry = new Map<string, HTMLCanvasElement>();
/** Accumulation buffers — one Float32 touch-count per pixel. */
const countRegistry = new Map<string, Float32Array>();
/**
 * Persistent per-layer pixel buffer reused across renderCountsToCanvas calls.
 * Avoids allocating a fresh 1MB ImageData on every touch sample — that
 * allocation churn was the main driver of lag building up over a long stroke.
 */
const imageDataRegistry = new Map<string, ImageData>();
/**
 * Per-colour-instance bitmaps for a PSD-imported layer whose region was
 * exported with `tools.colourInstancesNeeded > 1` (one "Paint:Style<N>"
 * layer per instance in Procreate). Keyed by layer id, then by
 * 1-based colour instance number. Absent for every ordinary layer (hand-drawn,
 * or a single-bitmap import) — those just use `canvasRegistry` as normal.
 * `canvasRegistry` still holds a "default" entry for a multi-instance layer
 * too (instance 1), so anything that only wants *a* preview (thumbnails,
 * Sketch Guide) keeps working unchanged.
 */
const instanceRegistry = new Map<string, Map<number, HTMLCanvasElement>>();
/**
 * Tile-space rect [x0,y0,x1,y1] a PSD-imported REGION layer's bitmap maps
 * onto — set only for layers imported via the region-render path (a traced
 * fundamental-domain-like shape whose paint legitimately extends outside the
 * unit cell, see psdImport.ts's `mergeCanvases`/`ExtractedRegion.rect`).
 * Absent for every ordinary layer (hand-drawn, or a legacy whole-canvas
 * import), which callers should treat as "not a region layer — render it
 * through the normal clipped per-tile path". Presence of an entry here (not
 * the layer's `kind`) is what tileRenderer.ts uses to route a layer to the
 * unclipped region pass instead of the normal clipped one.
 */
const importRectRegistry = new Map<string, [number, number, number, number]>();
/**
 * Authored ink storage (colour travels with the stroke, AND is frozen at draw
 * time so selecting a new colour never recolours existing ink).
 *
 * Each ink layer holds an ordered list of paint GROUPS. A group has its own
 * frozen palette + colour count and, within it, one raster per ORIGIN colour-
 * class (the class of the cell each mark was drawn in, alpha geometry). Strokes
 * drawn under the same palette coalesce into one group; changing the colour (or
 * count) starts a new group. At render, a group's origin-class-k raster is
 * tinted, at a placement of class j, to `paletteHex(group.paletteIndices[(j -
 * k) mod group.count], isDark)` — so a mark keeps the ink colour in the cell
 * it was drawn in, its symmetry copies shift, and it all uses the group's OWN
 * frozen swatch indices rather than the live selection. Only the swatch
 * INDEX is frozen, never a resolved hex, so an existing group re-tints
 * correctly on a dark/light toggle (brief 10-colours: "the stored colour
 * remains the same, the display renderer applies the medium transformation").
 * A layer with a group here is "authored ink"; imported ink (canvasRegistry /
 * instanceRegistry) has none.
 */
export interface InkGroup {
  paletteIndices: number[];
  count: number;
  classes: Map<number, HTMLCanvasElement>;
}
const inkGroupRegistry = new Map<string, InkGroup[]>();
/**
 * Coloured-sketch per-origin-class count buffers (colour travels with the
 * stroke, same rule as ink). Class 0 lives in the layer's BASE count buffer
 * (`countRegistry[id]` — so single-colour sketch and old saves are unchanged);
 * classes ≥1 live under synthetic ids `id#k` in the same flat registries. This
 * set just records which extra classes (≥1) a sketch layer has, for lifecycle,
 * snapshotting and the render composite. A stamp is routed to the buffer for
 * the colour-class of the cell it was drawn in; at render, class-k is tinted
 * (at a placement of class j) to the muted sketch palette shifted by (j-k).
 */
const sketchClasses = new Map<string, Set<number>>();
/**
 * Coloured-sketch paint GROUPS (brief 10-colours) — colour travels with the
 * stroke AND is frozen at draw time, mirroring `InkGroup` exactly: once the
 * artist has engaged real colour (`tools.sketchColourActive`), every stamp
 * from then on is routed to the group whose frozen palette+count matches
 * what's live right now (coalescing same-colour strokes), never to the
 * legacy per-class buffers above — so a later colour or symmetry change
 * only affects what gets drawn NEXT, never marks already on screen. Self-
 * contained (own counts/canvas/img per origin class) rather than piggy-
 * backing on the flat count/canvas registries, since a layer may hold
 * several groups' worth of the SAME origin class (one per colour epoch).
 */
export interface SketchGroup {
  paletteIndices: number[];
  count: number;
  classes: Map<number, { counts: Float32Array; canvas: HTMLCanvasElement; img: ImageData }>;
}
const sketchGroupRegistry = new Map<string, SketchGroup[]>();

/** Buffer id used only for useDrawing's dirty-rect/flushDirty tracking during
 *  an active stroke — parsed back to the real (layer, group, class) by
 *  `renderCountsToCanvas`. Never stored as an actual registry key. */
function sketchGroupBufferId(layerId: string, groupIndex: number, cls: number): string {
  return `${layerId}##${groupIndex}#${cls}`;
}

function parseSketchGroupBufferId(id: string): { layerId: string; groupIndex: number; cls: number } | null {
  const m = /^(.*)##(\d+)#(-?\d+)$/.exec(id);
  if (!m) return null;
  return { layerId: m[1]!, groupIndex: Number(m[2]), cls: Number(m[3]) };
}

/** Positive modulo (colour-class arithmetic wraps negatives). */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** A saved group already has `paletteIndices` (current format) or only the
 *  pre-swatch-index `palette` (resolved hex, on-disk from before this fix) —
 *  migrate the latter once, on load, via a lossless hue reverse-lookup. */
function paletteIndicesFromLegacy(g: { paletteIndices?: number[]; palette?: string[] }): number[] {
  return g.paletteIndices ?? (g.palette ?? []).map(nearestIndexForHex);
}

function blankCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = TILE_RASTER;
  c.height = TILE_RASTER;
  return c;
}

function copyCanvas(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = blankCanvas();
  c.getContext('2d')!.drawImage(src, 0, 0);
  return c;
}

// ---------------------------------------------------------------------------
// Theme-aware render
// ---------------------------------------------------------------------------

/**
 * Resolve the current colour scheme from the document root. Returns true when a
 * `data-theme="dark"` attribute (or equivalent) is present. Adjust the selector
 * to match whatever mechanism Escherllate uses for theming.
 */
export function isDarkMode(): boolean {
  return document.documentElement.getAttribute('data-theme') === 'dark'
    || document.documentElement.classList.contains('dark');
}

/**
 * Sketch render themes.
 *
 * `k`   — saturation rate for the exponential curve `α = 1 − e^(−k·count)`.
 *          Higher k → fewer stamp passes to reach full opacity.
 * `rgb` — ink colour in the rendered canvas.
 */
export const SKETCH_THEMES = {
  light: { k: 4.0,  r: 0x2a, g: 0x2a, b: 0x2a },
  dark:  { k: 8.0, r: 0xe6, g: 0xf1, b: 0xff },
} as const;

/** The pixel loop shared by every count-buffer render, legacy or grouped. */
function paintCountsRegion(
  counts: Float32Array,
  img: ImageData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  theme: { k: number; r: number; g: number; b: number },
): void {
  const { k, r, g, b } = theme;
  for (let y = y0; y < y1; y++) {
    const rowOffset = y * TILE_RASTER;
    for (let x = x0; x < x1; x++) {
      const i = rowOffset + x;
      const alpha = 1 - Math.exp(-k * counts[i]!);
      const o = i * 4;
      img.data[o]     = r;
      img.data[o + 1] = g;
      img.data[o + 2] = b;
      img.data[o + 3] = Math.round(alpha * 255);
    }
  }
}

/** Inverse of paintCountsRegion's `alpha = 1 - exp(-k·count)` curve: the
 *  touch-count that reproduces a saved pixel's alpha under theme `k`. Loading a
 *  PNG back into a count buffer must store this, not the raw alpha — otherwise
 *  the first stroke's full count→canvas rebuild runs the curve a second time and
 *  the whole layer darkens (compounding each save/load cycle; worst at k=8 dark
 *  mode). Clamped so full-opacity alpha stays finite. */
function alphaToCount(alpha01: number, k: number): number {
  if (alpha01 <= 0) return 0;
  return -Math.log(Math.max(1e-4, 1 - alpha01)) / k;
}
function clampRect(
  rect: { x0: number; y0: number; x1: number; y1: number } | undefined,
): { x0: number; y0: number; x1: number; y1: number } | null {
  const x0 = rect ? Math.max(0, rect.x0) : 0;
  const y0 = rect ? Math.max(0, rect.y0) : 0;
  const x1 = rect ? Math.min(TILE_RASTER, rect.x1) : TILE_RASTER;
  const y1 = rect ? Math.min(TILE_RASTER, rect.y1) : TILE_RASTER;
  return x1 <= x0 || y1 <= y0 ? null : { x0, y0, x1, y1 };
}

/**
 * Rebuild (some or all of) the render canvas for one sketch layer from its
 * count buffer. Call after any mutation to counts (stamp, erase) and on
 * theme change.
 *
 * `rect` restricts the rebuild to a sub-rectangle of the tile (pixel coords,
 * exclusive upper bound) — pass it during an active stroke so each call costs
 * only the touched area, not the full TILE_RASTER². Omit it for a full
 * rebuild (theme change, initial dot, rollback).
 *
 * Reuses one persistent ImageData per layer rather than allocating a fresh
 * buffer per call — repeated allocation here was the main driver of lag
 * building up over the course of a long stroke (GC pressure on the input
 * thread). Only call this once per animation frame during a stroke, not per
 * touch sample — see useDrawing's dirty-rect + flushDirty pattern.
 *
 * `id` may also be a frozen colour-group buffer id (brief 10-colours —
 * `sketchGroupBufferId`); those are dispatched to `renderSketchGroupBuffer`
 * so useDrawing's dirty-rect/flushDirty mechanism works unchanged for both.
 */
export function renderCountsToCanvas(
  id: string,
  rect?: { x0: number; y0: number; x1: number; y1: number },
): void {
  const parsed = parseSketchGroupBufferId(id);
  if (parsed) {
    renderSketchGroupBuffer(parsed.layerId, parsed.groupIndex, parsed.cls, rect);
    return;
  }
  const counts = countRegistry.get(id);
  const canvas = canvasRegistry.get(id);
  if (!counts || !canvas) return;
  const ctx = canvas.getContext('2d')!;
  let img = imageDataRegistry.get(id);
  if (!img) {
    img = ctx.createImageData(TILE_RASTER, TILE_RASTER);
    imageDataRegistry.set(id, img);
  }
  const theme = isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light;
  const bounds = clampRect(rect);
  if (!bounds) return;
  paintCountsRegion(counts, img, bounds.x0, bounds.y0, bounds.x1, bounds.y1, theme);
  ctx.putImageData(img, 0, 0, bounds.x0, bounds.y0, bounds.x1 - bounds.x0, bounds.y1 - bounds.y0);
}

/** Repaint one frozen colour-group's origin-class canvas from its own count
 *  buffer — the grouped-sketch analogue of the block above. The underlying
 *  RGB here is thrown away by tintCanvas at placement time (only alpha
 *  matters), so this never needs to run on a theme toggle — only after a
 *  stamp/erase touches it. */
function renderSketchGroupBuffer(
  layerId: string,
  groupIndex: number,
  cls: number,
  rect?: { x0: number; y0: number; x1: number; y1: number },
): void {
  const entry = sketchGroupRegistry.get(layerId)?.[groupIndex]?.classes.get(cls);
  if (!entry) return;
  const theme = isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light;
  const bounds = clampRect(rect);
  if (!bounds) return;
  paintCountsRegion(entry.counts, entry.img, bounds.x0, bounds.y0, bounds.x1, bounds.y1, theme);
  entry.canvas
    .getContext('2d')!
    .putImageData(entry.img, 0, 0, bounds.x0, bounds.y0, bounds.x1 - bounds.x0, bounds.y1 - bounds.y0);
}

/**
 * Re-render all sketch layers (call when the colour scheme changes so existing
 * marks repaint in the new theme without touching the count buffers).
 */
export function rerenderAllSketchLayers(): void {
  for (const id of countRegistry.keys()) renderCountsToCanvas(id);
}

// ---------------------------------------------------------------------------
// Legacy hydration helpers (unchanged from canvas revision)
// ---------------------------------------------------------------------------

/** Rasterize legacy vector strokes into a layer canvas (migration). */
function rasterizeStrokes(canvas: HTMLCanvasElement, strokes: SketchStroke[]): void {
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(TILE_RASTER, 0, 0, TILE_RASTER, 0, 0);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of strokes) {
    const breakSet = s.breaks && s.breaks.length > 0 ? new Set(s.breaks) : null;
    ctx.globalAlpha = s.opacity;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.size;
    ctx.beginPath();
    for (let i = 0; i < s.points.length; i++) {
      const [x, y] = s.points[i]!;
      if (i === 0 || breakSet?.has(i)) ctx.moveTo(x, y);
      else if (!breakSet && Math.hypot(x - s.points[i - 1]![0], y - s.points[i - 1]![1]) > 0.45) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
}

/**
 * Hydrate a canvas from saved data.
 * Also back-populates the count buffer from the loaded PNG alpha channel so
 * that loaded marks can be erased and re-rendered correctly.
 * Loaded pixels are treated as a single stamp pass: alpha/255 → count.
 * This is approximate but good enough — existing saves weren't drawn with
 * counts, so we just want something reasonable rather than perfect fidelity.
 */
async function hydrateCanvas(id: string, canvas: HTMLCanvasElement, data: SketchLayerData): Promise<void> {
  if (data.png) {
    const img = new Image();
    img.src = data.png;
    try {
      await img.decode();
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER);
      // Back-populate counts from rendered alpha so erasing works on loaded marks.
      const pixels = ctx.getImageData(0, 0, TILE_RASTER, TILE_RASTER).data;
      const counts = getOrCreateCounts(id);
      const k = (isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light).k;
      for (let i = 0; i < counts.length; i++) {
        counts[i] = alphaToCount(pixels[i * 4 + 3]! / 255, k);
      }
      return;
    } catch {
      // corrupt png — fall through to strokes/blank
    }
  }
  if (data.strokes && data.strokes.length > 0) {
    rasterizeStrokes(canvas, data.strokes);
    // Same back-population for legacy stroke data.
    const ctx = canvas.getContext('2d')!;
    const pixels = ctx.getImageData(0, 0, TILE_RASTER, TILE_RASTER).data;
    const counts = getOrCreateCounts(id);
    const k = (isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light).k;
    for (let i = 0; i < counts.length; i++) {
      counts[i] = alphaToCount(pixels[i * 4 + 3]! / 255, k);
    }
  }
}

/**
 * Hydrate an INK layer canvas from saved data. Unlike hydrateCanvas (sketch),
 * this does NOT back-populate a touch-count buffer — ink layers are pure
 * direct-canvas colour artwork and must stay out of countRegistry, so a theme
 * toggle's count re-render never touches them (that shared-canvas coupling was
 * the "drawing sketch wipes ink" bug). Legacy stroke data isn't produced for
 * ink layers (they post-date the stroke model), so only the PNG path applies.
 */
async function hydrateInkCanvas(canvas: HTMLCanvasElement, data: SketchLayerData): Promise<void> {
  if (!data.png) return;
  const img = new Image();
  img.src = data.png;
  try {
    await img.decode();
    canvas.getContext('2d')!.drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER);
  } catch {
    // corrupt png — leave the layer blank
  }
}

/** Decode a saved PNG into a fresh 512² canvas, or null if it won't decode. */
async function decodePng(png: string): Promise<HTMLCanvasElement | null> {
  const img = new Image();
  img.src = png;
  try {
    await img.decode();
    const c = document.createElement('canvas');
    c.width = TILE_RASTER;
    c.height = TILE_RASTER;
    c.getContext('2d')!.drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER);
    return c;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useLayersStore = defineStore('layers', () => {
  const activeLayer = ref<ActiveLayer>('sketch');
  /** bumped on every committed content change — drives autosave + redraw */
  const revision = ref(0);

  // --- Reference ---
  const reference = reactive<ReferenceState & { editMode: boolean }>({
    ...defaultReference(),
    editMode: false,
  });

  // --- Raster layers (brief Bug 4: sketch and ink are separate types, never
  // mixed; up to 5 of each). Render/stack order is all sketch (bottom) then all
  // ink (top) then trace — see `rasterLayers`. A single undo stack spans both
  // kinds so cross-kind edits undo in the order they were made; each command is
  // self-describing (paint=counts=sketch, paint-canvas=ink, delete-layer carries
  // its kind). There is always ≥1 sketch layer; ink starts empty and a layer is
  // created on demand the first time the Ink tool is used. ---
  const sketchLayers = ref<SketchLayerMeta[]>([makeSketchLayer(1, 'sketch')]);
  const inkLayers = ref<SketchLayerMeta[]>([]);
  const activeSketchLayerId = ref(sketchLayers.value[0]!.id);
  const activeInkLayerId = ref<string | null>(null);
  const rasterUndo = ref<RasterCommand[]>([]);
  const rasterRedo = ref<RasterCommand[]>([]);

  /** Layers of a kind, and the matching active-id ref. */
  function kindArray(kind: RasterLayerKind): typeof sketchLayers {
    return kind === 'ink' ? inkLayers : sketchLayers;
  }

  /** Every raster layer in stack order: sketch (bottom) → ink (top). */
  const rasterLayers = computed<SketchLayerMeta[]>(() => [...sketchLayers.value, ...inkLayers.value]);

  function findRasterLayer(id: string): SketchLayerMeta | undefined {
    return sketchLayers.value.find((l) => l.id === id) ?? inkLayers.value.find((l) => l.id === id);
  }

  /** A layer's kind (sketch unless it's a known ink layer) — drives colour
   *  tiling (sketch never tints) in the shared placement planner. */
  function getLayerKind(id: string): RasterLayerKind {
    return inkLayers.value.some((l) => l.id === id) ? 'ink' : 'sketch';
  }

  const activeSketchLayer = computed(
    () => sketchLayers.value.find((l) => l.id === activeSketchLayerId.value) ?? sketchLayers.value[0]!,
  );

  /** The active layer for the CURRENT drawing mode (ink → active ink layer,
   *  otherwise the active sketch layer). This is what stroke/erase/fill target. */
  const activeRasterLayer = computed<SketchLayerMeta>(() => {
    if (activeLayer.value === 'ink') {
      return (
        inkLayers.value.find((l) => l.id === activeInkLayerId.value) ??
        inkLayers.value[0] ??
        activeSketchLayer.value
      );
    }
    return activeSketchLayer.value;
  });

  function getLayerCanvas(id: string): HTMLCanvasElement {
    let c = canvasRegistry.get(id);
    if (!c) {
      c = blankCanvas();
      canvasRegistry.set(id, c);
    }
    return c;
  }

  /** The colour-instance bitmap set for a layer, if it has one (PSD import
   *  with `tools.colourInstancesNeeded > 1` at export time) — see
   *  `instanceRegistry`'s doc comment above. Undefined for every ordinary
   *  layer, which callers should treat as "use `getLayerCanvas` as normal". */
  function getLayerInstances(id: string): ReadonlyMap<number, HTMLCanvasElement> | undefined {
    return instanceRegistry.get(id);
  }

  /** The tile-space rect a PSD-imported REGION layer's bitmap maps onto (see
   *  `importRectRegistry`'s doc comment above). Undefined for every ordinary
   *  layer — callers should treat that as "render through the normal clipped
   *  per-tile path", not as a region layer with an implicit default rect. */
  function getLayerImportRect(id: string): [number, number, number, number] | undefined {
    return importRectRegistry.get(id);
  }

  // --- Authored-ink paint groups (colour travels with the stroke, frozen at
  //     draw time so a later colour change never recolours existing ink) ---

  function palettesEqual(a: readonly number[], b: readonly number[]): boolean {
    return a.length === b.length && a.every((c, i) => c === b[i]);
  }

  /**
   * The raster to draw into for one (ink layer, swatch-index palette, count,
   * origin class): finds the group with a matching frozen palette+count (so
   * same-colour strokes coalesce) or appends a new one, then gets/creates
   * that group's origin-class raster. Snapshot the layer BEFORE the first
   * draw of a stroke (snapshotInkGroups) for undo.
   */
  function getInkDrawTarget(id: string, paletteIndices: readonly number[], count: number, cls: number): HTMLCanvasElement {
    let groups = inkGroupRegistry.get(id);
    if (!groups) {
      groups = [];
      inkGroupRegistry.set(id, groups);
    }
    let group = groups.find((g) => g.count === count && palettesEqual(g.paletteIndices, paletteIndices));
    if (!group) {
      group = { paletteIndices: [...paletteIndices], count, classes: new Map() };
      groups.push(group);
    }
    let c = group.classes.get(cls);
    if (!c) {
      c = blankCanvas();
      group.classes.set(cls, c);
    }
    return c;
  }

  /** This ink layer's paint groups, or undefined for non-authored ink. */
  function getInkGroups(id: string): readonly InkGroup[] | undefined {
    return inkGroupRegistry.get(id);
  }

  function hasInkGroups(id: string): boolean {
    const g = inkGroupRegistry.get(id);
    return !!g && g.some((grp) => grp.classes.size > 0);
  }

  /** Every class raster across all of a layer's groups (for erase). */
  function eachInkCanvas(id: string): HTMLCanvasElement[] {
    const groups = inkGroupRegistry.get(id);
    if (!groups) return [];
    return groups.flatMap((g) => [...g.classes.values()]);
  }

  /** Deep-copy a layer's paint groups (undo / delete snapshots). */
  function snapshotInkGroups(id: string): InkGroup[] {
    const groups = inkGroupRegistry.get(id);
    if (!groups) return [];
    return groups.map((g) => ({
      paletteIndices: [...g.paletteIndices],
      count: g.count,
      classes: new Map([...g.classes].map(([cls, canvas]) => [cls, copyCanvas(canvas)])),
    }));
  }

  /** Replace a layer's paint groups from a snapshot (copies, so the snapshot
   *  stays reusable for redo). */
  function restoreInkGroups(id: string, snap: InkGroup[]): void {
    inkGroupRegistry.set(
      id,
      snap.map((g) => ({
        paletteIndices: [...g.paletteIndices],
        count: g.count,
        classes: new Map([...g.classes].map(([cls, canvas]) => [cls, copyCanvas(canvas)])),
      })),
    );
  }

  /**
   * Flatten an authored ink layer's groups into one canvas — geometry, tinted
   * as if viewed at the identity placement (class j=0), each group using its
   * OWN frozen palette. Used for thumbnails and the flood-fill boundary read.
   */
  function compositeInkLayer(id: string): HTMLCanvasElement {
    const out = blankCanvas();
    const groups = inkGroupRegistry.get(id);
    if (!groups) return out;
    const ctx = out.getContext('2d')!;
    const dark = isDarkMode();
    for (const g of groups) {
      for (const [cls, canvas] of g.classes) {
        const index = g.count > 1 ? g.paletteIndices[mod(-cls, g.count)] ?? g.paletteIndices[0]! : g.paletteIndices[0]!;
        ctx.drawImage(tintByAlpha(canvas, paletteHex(index, dark)), 0, 0);
      }
    }
    return out;
  }

  /** Flatten a sketch layer's frozen colour paint groups (brief 10-colours)
   *  into one canvas — same rule as compositeInkLayer, each group tinted with
   *  its OWN palette as if viewed at the identity placement. Used for
   *  thumbnails, drawn OVER the layer's legacy (pre-colour) canvas — groups
   *  hold real colour content the legacy canvas never sees once active. */
  function compositeSketchGroups(id: string): HTMLCanvasElement {
    const out = blankCanvas();
    const groups = sketchGroupRegistry.get(id);
    if (!groups) return out;
    const ctx = out.getContext('2d')!;
    const dark = isDarkMode();
    for (const g of groups) {
      for (const [cls, entry] of g.classes) {
        const index = g.count > 1 ? g.paletteIndices[mod(-cls, g.count)] ?? g.paletteIndices[0]! : g.paletteIndices[0]!;
        ctx.drawImage(tintByAlpha(entry.canvas, paletteHex(index, dark)), 0, 0);
      }
    }
    return out;
  }

  /** Push one undoable authored-ink edit (stroke / fill / erase). */
  function commitInk(id: string, before: InkGroup[]): void {
    rasterUndo.value.push({ kind: 'paint-ink', layerId: id, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Get or create the count buffer for a sketch layer. */
  function getLayerCounts(id: string): Float32Array {
    return getOrCreateCounts(id);
  }

  // --- Coloured-sketch per-origin-class count buffers ---

  /** Count-buffer id for a sketch layer's colour class: class 0 is the base
   *  layer buffer (backward compatible), classes ≥1 are synthetic `id#k`. */
  function sketchCountId(layerId: string, cls: number): string {
    return cls <= 0 ? layerId : `${layerId}#${cls}`;
  }

  /** Get/create the count buffer for one (sketch layer, origin class), recording
   *  the class (≥1) and ensuring its render-target canvas exists.
   *
   *  The canvas is created for EVERY class including 0 (brief 08 §2): the
   *  multi-colour render path reads canvases via the get-only `sketchClassCanvas`
   *  and skips any class whose canvas is missing, so if class 0's canvas wasn't
   *  created here (e.g. right after a clear, which wipes the registries and makes
   *  a fresh layer) its strokes would accumulate in the count buffer but never
   *  paint — invisible until a single-colour mode switch lazily created the
   *  canvas. Creating it up front for class 0 too fixes that. */
  function getSketchCounts(layerId: string, cls: number): Float32Array {
    if (cls > 0) {
      let set = sketchClasses.get(layerId);
      if (!set) {
        set = new Set();
        sketchClasses.set(layerId, set);
      }
      set.add(cls);
    }
    getLayerCanvas(sketchCountId(layerId, cls)); // renderCountsToCanvas needs a target — for class 0 too
    return getOrCreateCounts(sketchCountId(layerId, cls));
  }

  /** Extra colour classes (≥1) this sketch layer has buffers for; class 0 is
   *  always the base buffer. Ascending. */
  function sketchClassList(layerId: string): number[] {
    return [0, ...[...(sketchClasses.get(layerId) ?? [])].sort((a, b) => a - b)];
  }

  /** The rendered (grey) canvas for a sketch layer's class, if it exists. */
  function sketchClassCanvas(layerId: string, cls: number): HTMLCanvasElement | undefined {
    return canvasRegistry.get(sketchCountId(layerId, cls));
  }

  /** Snapshot every count buffer of a sketch layer (base + each class ≥1), keyed
   *  by count id — for coloured-sketch undo. */
  function snapshotSketch(layerId: string): Map<string, Float32Array> {
    const snap = new Map<string, Float32Array>();
    for (const cls of sketchClassList(layerId)) {
      const cid = sketchCountId(layerId, cls);
      snap.set(cid, new Float32Array(getOrCreateCounts(cid)));
    }
    return snap;
  }

  /** Restore a sketch layer's count buffers from a snapshot and re-render each;
   *  any class buffer created after the snapshot is zeroed (rolled back). */
  function restoreSketch(layerId: string, snap: Map<string, Float32Array>): void {
    for (const cls of sketchClassList(layerId)) {
      const cid = sketchCountId(layerId, cls);
      const buf = getOrCreateCounts(cid);
      const snapBuf = snap.get(cid);
      if (snapBuf) buf.set(snapBuf);
      else buf.fill(0);
      renderCountsToCanvas(cid);
    }
    for (const [cid, buf] of snap) {
      if (!countRegistry.has(cid)) {
        getLayerCanvas(cid);
        getOrCreateCounts(cid).set(buf);
        renderCountsToCanvas(cid);
        // Re-register any class ≥1 the snapshot restored, so sketchClassList
        // includes it and the multi-colour render iterates it (brief 08 §2 —
        // otherwise the buffer+canvas exist but the class is absent from the
        // list and never drawn). cid is `${layerId}#${cls}` for classes ≥1.
        const hash = cid.lastIndexOf('#');
        if (hash > 0) {
          const cls = Number(cid.slice(hash + 1));
          const lid = cid.slice(0, hash);
          if (Number.isInteger(cls) && cls > 0) {
            let set = sketchClasses.get(lid);
            if (!set) {
              set = new Set();
              sketchClasses.set(lid, set);
            }
            set.add(cls);
          }
        }
      }
    }
  }

  /** Push one undoable coloured-sketch edit (stroke / fill / erase). */
  function commitSketch(layerId: string, before: Map<string, Float32Array>): void {
    rasterUndo.value.push({ kind: 'paint-sketch', layerId, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  // --- Coloured-sketch paint GROUPS (brief 10-colours: frozen at draw time,
  //     mirrors the authored-ink group functions above) ---

  /**
   * The count buffer to stamp into for one (sketch layer, palette, count,
   * origin class) once real colour is active: finds the group with a
   * matching frozen palette+count (so same-colour strokes coalesce) or
   * appends a new one, then gets/creates that group's origin-class buffer.
   * Returns the buffer AND its dirty-tracking id (for useDrawing's
   * currentCountId/flushDirty, which stays generic across legacy + grouped
   * buffers). Snapshot the layer BEFORE the first stamp of a stroke
   * (snapshotSketchGroups) for undo.
   */
  function getSketchGroupCounts(
    layerId: string,
    paletteIndices: readonly number[],
    count: number,
    cls: number,
  ): { counts: Float32Array; id: string } {
    let groups = sketchGroupRegistry.get(layerId);
    if (!groups) {
      groups = [];
      sketchGroupRegistry.set(layerId, groups);
    }
    let groupIndex = groups.findIndex((g) => g.count === count && palettesEqual(g.paletteIndices, paletteIndices));
    if (groupIndex === -1) {
      groups.push({ paletteIndices: [...paletteIndices], count, classes: new Map() });
      groupIndex = groups.length - 1;
    }
    const group = groups[groupIndex]!;
    let entry = group.classes.get(cls);
    if (!entry) {
      const canvas = blankCanvas();
      const img = canvas.getContext('2d')!.createImageData(TILE_RASTER, TILE_RASTER);
      entry = { counts: new Float32Array(TILE_RASTER * TILE_RASTER), canvas, img };
      group.classes.set(cls, entry);
    }
    return { counts: entry.counts, id: sketchGroupBufferId(layerId, groupIndex, cls) };
  }

  /** This sketch layer's colour paint groups, or undefined if none yet. */
  function getSketchGroups(id: string): readonly SketchGroup[] | undefined {
    return sketchGroupRegistry.get(id);
  }

  function hasSketchGroups(id: string): boolean {
    const g = sketchGroupRegistry.get(id);
    return !!g && g.some((grp) => grp.classes.size > 0);
  }

  /** Every group's origin-class count buffer for a layer, with its render-
   *  trigger id (for erase — the eraser removes whatever's visually there,
   *  any colour epoch — and must re-render each buffer it touches). */
  function eachSketchGroupCounts(id: string): { counts: Float32Array; id: string }[] {
    const groups = sketchGroupRegistry.get(id);
    if (!groups) return [];
    return groups.flatMap((g, gi) =>
      [...g.classes.entries()].map(([cls, entry]) => ({
        counts: entry.counts,
        id: sketchGroupBufferId(id, gi, cls),
      })),
    );
  }

  /** Deep-copy a layer's colour paint groups (undo / delete snapshots). */
  function snapshotSketchGroups(id: string): SketchGroup[] {
    const groups = sketchGroupRegistry.get(id);
    if (!groups) return [];
    return groups.map((g) => ({
      paletteIndices: [...g.paletteIndices],
      count: g.count,
      classes: new Map(
        [...g.classes].map(([cls, entry]) => {
          const canvas = copyCanvas(entry.canvas);
          const img = canvas.getContext('2d')!.createImageData(TILE_RASTER, TILE_RASTER);
          return [cls, { counts: new Float32Array(entry.counts), canvas, img }];
        }),
      ),
    }));
  }

  /** Replace a layer's colour paint groups from a snapshot (copies, so the
   *  snapshot stays reusable for redo). */
  function restoreSketchGroups(id: string, snap: SketchGroup[]): void {
    sketchGroupRegistry.set(
      id,
      snap.map((g) => ({
        paletteIndices: [...g.paletteIndices],
        count: g.count,
        classes: new Map(
          [...g.classes].map(([cls, entry]) => {
            const canvas = copyCanvas(entry.canvas);
            const img = canvas.getContext('2d')!.createImageData(TILE_RASTER, TILE_RASTER);
            return [cls, { counts: new Float32Array(entry.counts), canvas, img }];
          }),
        ),
      })),
    );
  }

  /** Push one undoable coloured-sketch-group edit (stroke / fill / erase). */
  function commitSketchGroups(id: string, before: SketchGroup[]): void {
    rasterUndo.value.push({ kind: 'paint-sketch-groups', layerId: id, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Read-only twin of `disposeSketchClasses` — snapshots a sketch layer's
   *  extra (≥1) class buffers without removing them (clear-all, which wipes
   *  every registry itself right after capturing every layer). */
  function captureSketchClasses(layerId: string): Map<number, Float32Array> | undefined {
    const set = sketchClasses.get(layerId);
    if (!set || set.size === 0) return undefined;
    const snap = new Map<number, Float32Array>();
    for (const k of set) {
      const counts = countRegistry.get(sketchCountId(layerId, k));
      if (counts) snap.set(k, new Float32Array(counts));
    }
    return snap.size ? snap : undefined;
  }

  /** Non-destructive full-content snapshot of one raster layer, for clear-all. */
  function captureLayerSnapshot(layerMeta: SketchLayerMeta): LayerSnapshot {
    const id = layerMeta.id;
    return {
      layer: { ...layerMeta },
      canvas: markRaw(snapshotLayer(id)),
      counts: markRaw(hasCountBuffer(id) ? new Float32Array(getOrCreateCounts(id)) : new Float32Array(0)),
      sketchClasses: captureSketchClasses(id),
      sketchGroups: hasSketchGroups(id) ? markRaw(snapshotSketchGroups(id)) : undefined,
      inkGroups: hasInkGroups(id) ? markRaw(snapshotInkGroups(id)) : undefined,
    };
  }

  /** Delete a sketch layer's colour paint groups. Returns a snapshot of them
   *  for delete-layer undo (self-contained, unlike disposeSketchClasses —
   *  nothing to remove from the flat registries). */
  function disposeSketchGroups(layerId: string): SketchGroup[] {
    const snap = snapshotSketchGroups(layerId);
    sketchGroupRegistry.delete(layerId);
    return snap;
  }

  /** Delete a sketch layer's extra (≥1) class buffers from every registry.
   *  Returns a snapshot of them (class→counts) for delete-layer undo. */
  function disposeSketchClasses(layerId: string): Map<number, Float32Array> {
    const snap = new Map<number, Float32Array>();
    const set = sketchClasses.get(layerId);
    if (set) {
      for (const k of set) {
        const cid = sketchCountId(layerId, k);
        const counts = countRegistry.get(cid);
        if (counts) snap.set(k, new Float32Array(counts));
        countRegistry.delete(cid);
        canvasRegistry.delete(cid);
        imageDataRegistry.delete(cid);
      }
      sketchClasses.delete(layerId);
    }
    return snap;
  }

  /**
   * True when `id` already has a touch-count buffer registered. Callers that
   * only want to know "does this layer have any *drawn* content" (as opposed
   * to imported artwork) must check this BEFORE calling `getLayerCounts` —
   * `getLayerCounts` lazily creates a blank buffer for any id, and once an
   * imported (canvas-only) layer's id is registered there, a later theme
   * toggle (`rerenderAllSketchLayers`) would re-render its canvas FROM that
   * all-zero buffer, wiping the imported bitmap. See psdExport.ts's
   * `hasVisibleSketchContent` for the one place this bit us during review.
   */
  function hasCountBuffer(id: string): boolean {
    return countRegistry.has(id);
  }

  /** Snapshot the count buffer (for undo). O(TILE_RASTER²) copy. */
  function snapshotCounts(id: string): Float32Array {
    return new Float32Array(getOrCreateCounts(id));
  }

  /**
   * Legacy canvas snapshot — kept for delete-layer undo only (the canvas is
   * still needed to restore the render on undo). Paint undo now uses count
   * snapshots exclusively.
   */
  function snapshotLayer(id: string): HTMLCanvasElement {
    return copyCanvas(getLayerCanvas(id));
  }

  /** Push one undoable raster edit (pen stroke, eraser drag, …). */
  function commitRaster(layerId: string, before: Float32Array): void {
    rasterUndo.value.push({ kind: 'paint', layerId, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /**
   * Push one undoable direct-canvas edit (ink bucket fill). `before` is a canvas
   * snapshot taken *before* the edit — ink content lives on the layer canvas, not
   * the count buffer, so it must be restored by blitting rather than re-rendering
   * counts (which would wipe direct-painted ink).
   */
  function commitRasterCanvas(layerId: string, before: HTMLCanvasElement): void {
    rasterUndo.value.push({ kind: 'paint-canvas', layerId, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Restore a layer's count buffer from a snapshot, then re-render its canvas. */
  function restore(layerId: string, snap: Float32Array): void {
    const counts = getOrCreateCounts(layerId);
    counts.set(snap);
    renderCountsToCanvas(layerId);
  }

  /** Restore a layer canvas from a snapshot (direct-canvas / ink undo). */
  function restoreCanvas(layerId: string, snap: HTMLCanvasElement): void {
    const canvas = getLayerCanvas(layerId);
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, TILE_RASTER, TILE_RASTER);
    ctx.drawImage(snap, 0, 0);
  }

  function undoSketch(): void {
    const cmd = rasterUndo.value.pop();
    if (!cmd) return;
    if (cmd.kind === 'delete-layer') {
      const arr = kindArray(cmd.layer.kind);
      const idx = Math.min(cmd.index, arr.value.length);
      arr.value.splice(idx, 0, { ...cmd.layer });
      canvasRegistry.set(cmd.layer.id, cmd.canvas);
      // Ink has no count buffer — only restore one for sketch layers, or a
      // theme toggle would re-render the restored ink canvas from zeros.
      if (cmd.layer.kind === 'sketch') {
        countRegistry.set(cmd.layer.id, new Float32Array(cmd.counts));
        // Restore the extra colour-class count buffers too (coloured sketch).
        if (cmd.sketchClasses) {
          const set = new Set<number>();
          for (const [k, buf] of cmd.sketchClasses) {
            set.add(k);
            countRegistry.set(sketchCountId(cmd.layer.id, k), new Float32Array(buf));
            getLayerCanvas(sketchCountId(cmd.layer.id, k));
            renderCountsToCanvas(sketchCountId(cmd.layer.id, k));
          }
          if (set.size) sketchClasses.set(cmd.layer.id, set);
        }
        if (cmd.sketchGroups) restoreSketchGroups(cmd.layer.id, cmd.sketchGroups);
      }
      if (cmd.inkGroups) restoreInkGroups(cmd.layer.id, cmd.inkGroups);
      if (cmd.layer.kind === 'ink') activeInkLayerId.value = cmd.layer.id;
      else activeSketchLayerId.value = cmd.layer.id;
      rasterRedo.value.push(cmd);
    } else if (cmd.kind === 'paint-canvas') {
      cmd.after = markRaw(snapshotLayer(cmd.layerId));
      restoreCanvas(cmd.layerId, cmd.before);
      rasterRedo.value.push(cmd);
    } else if (cmd.kind === 'paint-ink') {
      cmd.after = markRaw(snapshotInkGroups(cmd.layerId));
      restoreInkGroups(cmd.layerId, cmd.before);
      rasterRedo.value.push(cmd);
    } else if (cmd.kind === 'paint-sketch') {
      cmd.after = markRaw(snapshotSketch(cmd.layerId));
      restoreSketch(cmd.layerId, cmd.before);
      rasterRedo.value.push(cmd);
    } else if (cmd.kind === 'paint-sketch-groups') {
      cmd.after = markRaw(snapshotSketchGroups(cmd.layerId));
      restoreSketchGroups(cmd.layerId, cmd.before);
      rasterRedo.value.push(cmd);
    } else if (cmd.kind === 'clear-raster') {
      cmd.after = markRaw(captureLayerSnapshot(cmd.before.layer));
      wipeLayerContent(cmd.layerId);
      applyLayerSnapshot(cmd.before);
      rasterRedo.value.push(cmd);
    } else {
      cmd.after = markRaw(snapshotCounts(cmd.layerId));
      restore(cmd.layerId, cmd.before);
      rasterRedo.value.push(cmd);
    }
    isDirty.value = true;
    revision.value++;
  }

  function redoSketch(): void {
    const cmd = rasterRedo.value.pop();
    if (!cmd) return;
    if (cmd.kind === 'delete-layer') {
      const arr = kindArray(cmd.layer.kind);
      arr.value = arr.value.filter((l) => l.id !== cmd.layer.id);
      canvasRegistry.delete(cmd.layer.id);
      countRegistry.delete(cmd.layer.id);
      inkGroupRegistry.delete(cmd.layer.id);
      disposeSketchClasses(cmd.layer.id);
      disposeSketchGroups(cmd.layer.id);
      if (cmd.layer.kind === 'ink') {
        if (activeInkLayerId.value === cmd.layer.id) {
          activeInkLayerId.value = inkLayers.value[inkLayers.value.length - 1]?.id ?? null;
        }
      } else if (activeSketchLayerId.value === cmd.layer.id) {
        activeSketchLayerId.value = sketchLayers.value[sketchLayers.value.length - 1]!.id;
      }
      rasterUndo.value.push(cmd);
    } else if (cmd.kind === 'paint-canvas') {
      if (!cmd.after) return;
      restoreCanvas(cmd.layerId, cmd.after);
      rasterUndo.value.push(cmd);
    } else if (cmd.kind === 'paint-ink') {
      if (!cmd.after) return;
      restoreInkGroups(cmd.layerId, cmd.after);
      rasterUndo.value.push(cmd);
    } else if (cmd.kind === 'paint-sketch') {
      if (!cmd.after) return;
      restoreSketch(cmd.layerId, cmd.after);
      rasterUndo.value.push(cmd);
    } else if (cmd.kind === 'paint-sketch-groups') {
      if (!cmd.after) return;
      restoreSketchGroups(cmd.layerId, cmd.after);
      rasterUndo.value.push(cmd);
    } else if (cmd.kind === 'clear-raster') {
      wipeLayerContent(cmd.layerId);
      if (cmd.after) applyLayerSnapshot(cmd.after);
      rasterUndo.value.push(cmd);
    } else {
      if (!cmd.after) return;
      restore(cmd.layerId, cmd.after);
      rasterUndo.value.push(cmd);
    }
    isDirty.value = true;
    revision.value++;
  }

  function addRasterLayer(kind: RasterLayerKind): SketchLayerMeta | null {
    const arr = kindArray(kind);
    const cap = kind === 'ink' ? MAX_INK_LAYERS : MAX_SKETCH_LAYERS;
    if (arr.value.length >= cap) return null;
    const maxN = arr.value.reduce((n, l) => {
      const m = /(\d+)$/.exec(l.name);
      return Math.max(n, m ? Number(m[1]) : 0);
    }, 0);
    const layer = makeSketchLayer(maxN + 1, kind);
    arr.value.push(layer);
    if (kind === 'ink') activeInkLayerId.value = layer.id;
    else activeSketchLayerId.value = layer.id;
    revision.value++;
    return layer;
  }

  function addSketchLayer(): SketchLayerMeta | null {
    return addRasterLayer('sketch');
  }

  function addInkLayer(): SketchLayerMeta | null {
    return addRasterLayer('ink');
  }

  /**
   * Ensure the given drawing mode has an active layer to draw on, creating one
   * if that kind has none yet (brief Bug 4: "return to the last used layer of
   * that type, or create a new one if none exists yet"). The active id is
   * sticky per kind, so switching away and back returns to the same layer.
   */
  function ensureLayerForKind(kind: RasterLayerKind): void {
    const arr = kindArray(kind);
    const activeId = kind === 'ink' ? activeInkLayerId : activeSketchLayerId;
    if (!arr.value.some((l) => l.id === activeId.value)) {
      activeId.value = arr.value[arr.value.length - 1]?.id ?? null;
    }
    if (activeId.value === null) addRasterLayer(kind);
  }

  /** Set the drawing mode, ensuring a raster layer of that kind exists first. */
  function setMode(mode: ActiveLayer): void {
    if (mode === 'sketch' || mode === 'ink') ensureLayerForKind(mode);
    activeLayer.value = mode;
  }

  /** Layer-panel row click: make this layer active AND switch to its mode. */
  function selectRasterLayer(id: string): void {
    const layer = findRasterLayer(id);
    if (!layer) return;
    if (layer.kind === 'ink') {
      activeInkLayerId.value = id;
      activeLayer.value = 'ink';
    } else {
      activeSketchLayerId.value = id;
      activeLayer.value = 'sketch';
    }
  }

  /** Layer-panel "Edit" button: resume editing an already-committed Trace
   *  layer — the store-state half of useTraceEditor's `editLayer` (which also
   *  resets the editor's own mode/selection state; call that, not this,
   *  from the UI). Unlike `startTraceLayer`, does NOT create a layer — it
   *  must already exist in `traceLayers`. */
  function beginEditingTraceLayer(id: string): void {
    if (!traceLayers.value.some((l) => l.id === id)) return;
    activeLayer.value = 'trace';
    editingTraceLayerId.value = id;
  }

  /**
   * Add a new raster layer pre-populated with existing pixel content (PSD
   * import — brief 03-procreate.md/04-clarify.md). Unlike a normal sketch
   * layer, an imported layer's canvas is NOT derived from a touch-count
   * buffer: it holds full-colour painted artwork as-is, so it is deliberately
   * kept OUT of `countRegistry`. `renderCountsToCanvas`/`rerenderAllSketchLayers`
   * only touch ids present there, so the imported bitmap survives theme
   * toggles untouched. (If the user later draws on it with the Sketch/Ink pen,
   * the normal count-buffer render WILL overwrite it on that layer's next
   * repaint — a known limitation of the shared single-raster-stack model this
   * store already documents at the top of this file; flagged, not fixed,
   * here. The same caveat applies to `removeSketchLayer`'s delete/undo path:
   * it snapshots a counts buffer unconditionally for the undo command, so
   * deleting-then-undoing an imported layer also registers it in
   * `countRegistry` and exposes it to the next theme toggle. Anything that
   * only wants to CHECK for content without risking this — psdExport.ts's
   * "has this layer got anything in it" gate, for one — must use
   * `hasCountBuffer` before calling `getLayerCounts`, never the reverse.)
   *
   * Deliberately bypasses MAX_SKETCH_LAYERS: a bulk import must not silently
   * drop the artist's Procreate work just because it exceeds the hand-drawing
   * UI's 5-layer cap.
   */
  function addImportedRasterLayer(
    name: string,
    canvas: HTMLCanvasElement,
    rect?: [number, number, number, number],
  ): SketchLayerMeta {
    // Imported PSD artwork is full-colour painted content → an ink-kind layer
    // (direct canvas, no count buffer, colour-preserving), never a graphite
    // sketch layer.
    const layer: SketchLayerMeta = { id: newId(), name, opacity: 1, visible: true, kind: 'ink' };
    inkLayers.value.push(layer);
    canvasRegistry.set(layer.id, canvas);
    if (rect) importRectRegistry.set(layer.id, rect);
    activeInkLayerId.value = layer.id;
    isDirty.value = true;
    revision.value++;
    return layer;
  }

  /**
   * Sibling of `addImportedRasterLayer` for a region that was exported with
   * more than one colour instance (`tools.colourInstancesNeeded > 1` — one
   * "Paint:Style<N>" layer per instance in Procreate). The
   * artist painted each instance's true colours separately, so at render
   * time the matching instance's bitmap is used directly per placement (no
   * runtime tinting) — see the shared `planPlacementBitmap` in
   * services/layerPlacementBitmap.ts, used by both TileCanvas.vue and the
   * offscreen export renderer.
   *
   * `instances` is keyed by the same 1-based colour-instance numbering
   * psdExport.ts writes into the layer name (Style1, Style2, ...).
   * `canvasRegistry` still gets a "default" entry (the lowest instance
   * number available) so anything that only wants *a* preview keeps working
   * unchanged (thumbnails, Sketch Guide, and this layer's own entry in the
   * layer panel).
   */
  function addImportedRasterLayerSet(
    name: string,
    instances: Record<number, HTMLCanvasElement>,
    rect?: [number, number, number, number],
  ): SketchLayerMeta {
    const layer: SketchLayerMeta = { id: newId(), name, opacity: 1, visible: true, kind: 'ink' };
    inkLayers.value.push(layer);
    const map = new Map<number, HTMLCanvasElement>(
      Object.entries(instances).map(([k, v]): [number, HTMLCanvasElement] => [Number(k), v]),
    );
    instanceRegistry.set(layer.id, map);
    if (rect) importRectRegistry.set(layer.id, rect);
    const fallback = map.get(Math.min(...map.keys()));
    if (fallback) canvasRegistry.set(layer.id, fallback);
    activeInkLayerId.value = layer.id;
    isDirty.value = true;
    revision.value++;
    return layer;
  }

  function removeRasterLayer(id: string): void {
    const layerMeta = findRasterLayer(id);
    if (!layerMeta) return;
    const kind = layerMeta.kind;
    const arr = kindArray(kind);
    // Always keep at least one sketch layer (the default drawing surface); ink
    // may be emptied entirely (it's created on demand).
    if (kind === 'sketch' && arr.value.length <= 1) return;
    const index = arr.value.findIndex((l) => l.id === id);
    if (index === -1) return;
    const layer = { ...arr.value[index]! };
    const canvas = canvasRegistry.get(id) ?? blankCanvas();
    // Ink has no count buffer — don't call getOrCreateCounts on it (that would
    // register a blank buffer and expose the ink canvas to theme re-render).
    const counts = countRegistry.has(id) ? new Float32Array(getOrCreateCounts(id)) : new Float32Array(0);
    const inkGroups = hasInkGroups(id) ? snapshotInkGroups(id) : undefined;
    // Snapshot + remove the sketch layer's extra colour-class buffers/groups.
    const sketchClassSnap = disposeSketchClasses(id);
    const sketchGroupSnap = disposeSketchGroups(id);
    arr.value = arr.value.filter((l) => l.id !== id);
    canvasRegistry.delete(id);
    countRegistry.delete(id);
    instanceRegistry.delete(id);
    importRectRegistry.delete(id);
    inkGroupRegistry.delete(id);
    imageDataRegistry.delete(id);
    if (kind === 'ink') {
      if (activeInkLayerId.value === id) activeInkLayerId.value = arr.value[arr.value.length - 1]?.id ?? null;
    } else if (activeSketchLayerId.value === id) {
      activeSketchLayerId.value = arr.value[arr.value.length - 1]!.id;
    }
    rasterUndo.value = rasterUndo.value.filter((c) => c.kind === 'delete-layer' || c.layerId !== id);
    rasterUndo.value.push({
      kind: 'delete-layer',
      layer,
      canvas: markRaw(canvas),
      counts: markRaw(counts),
      sketchClasses: sketchClassSnap.size ? markRaw(sketchClassSnap) : undefined,
      sketchGroups: sketchGroupSnap.length ? markRaw(sketchGroupSnap) : undefined,
      inkGroups: inkGroups ? markRaw(inkGroups) : undefined,
      index,
    });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Kept as the historical name (sketch + ink both route through it now). */
  function removeSketchLayer(id: string): void {
    removeRasterLayer(id);
  }

  /** Clear a raster layer's drawn content but keep the layer itself (Layers
   *  panel per-layer erase button). Undoable as a single step. */
  function clearRasterLayer(id: string): void {
    const layerMeta = findRasterLayer(id);
    if (!layerMeta) return;
    const before = captureLayerSnapshot(layerMeta);
    wipeLayerContent(id);
    rasterUndo.value.push({ kind: 'clear-raster', layerId: id, before: markRaw(before), after: null });
    if (rasterUndo.value.length > RASTER_UNDO_DEPTH) rasterUndo.value.shift();
    rasterRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Reorder a raster layer within its own kind (brief 09-layout: the Layers
   *  panel's up/down arrows). The panel displays each kind's array reversed
   *  (top-of-stack shown first), so display-"up" is a HIGHER index here (closer
   *  to the end/top of the underlying stack) and display-"down" is lower. A
   *  no-op at either end. Not undoable — deliberately simple, and instantly
   *  reversible with the opposite arrow. */
  function moveRasterLayer(id: string, dir: 'up' | 'down'): void {
    const layerMeta = findRasterLayer(id);
    if (!layerMeta) return;
    const arr = kindArray(layerMeta.kind);
    const i = arr.value.findIndex((l) => l.id === id);
    const j = dir === 'up' ? i + 1 : i - 1;
    if (i === -1 || j < 0 || j >= arr.value.length) return;
    const next = [...arr.value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    arr.value = next;
    isDirty.value = true;
    revision.value++;
  }

  function serializeKind(arr: SketchLayerMeta[]): SketchLayerData[] {
    return arr.map((l) => {
      // Authored ink round-trips as paint groups (colour travels with the
      // stroke, frozen per group); the flattened `png` is left null since
      // reload rebuilds from the groups.
      if (l.kind === 'ink' && hasInkGroups(l.id)) {
        const groups = inkGroupRegistry.get(l.id)!;
        const inkGroups = groups.map((g) => ({
          paletteIndices: [...g.paletteIndices],
          count: g.count,
          classes: [...g.classes.entries()].map(([cls, canvas]) => ({ cls, png: canvas.toDataURL('image/png') })),
        }));
        return { id: l.id, name: l.name, opacity: l.opacity, visible: l.visible, kind: 'ink' as const, png: null, inkGroups };
      }
      // Coloured sketch round-trips as per-origin-class canvases (colour travels
      // with the stroke); the base canvas (`png`) doubles as class 0.
      const extra = sketchClasses.get(l.id);
      const canvas = canvasRegistry.get(l.id);
      const base = {
        id: l.id,
        name: l.name,
        opacity: l.opacity,
        visible: l.visible,
        kind: l.kind,
        png: canvas ? canvas.toDataURL('image/png') : null,
      };
      const withClasses =
        l.kind === 'sketch' && extra && extra.size
          ? {
              ...base,
              sketchClassPngs: [...extra]
                .sort((a, b) => a - b)
                .map((cls) => {
                  const c = canvasRegistry.get(sketchCountId(l.id, cls));
                  return { cls, png: c ? c.toDataURL('image/png') : null };
                })
                .filter((e): e is { cls: number; png: string } => e.png !== null),
            }
          : base;
      // Frozen colour paint groups round-trip alongside the legacy graphite
      // content above (brief 10-colours — a later colour change must never
      // recolour these, same rule as ink).
      if (l.kind === 'sketch' && hasSketchGroups(l.id)) {
        const groups = sketchGroupRegistry.get(l.id)!;
        const sketchColourGroups = groups.map((g) => ({
          paletteIndices: [...g.paletteIndices],
          count: g.count,
          classes: [...g.classes.entries()].map(([cls, entry]) => ({ cls, png: entry.canvas.toDataURL('image/png') })),
        }));
        return { ...withClasses, sketchColourGroups };
      }
      return withClasses;
    });
  }

  /** Serialize sketch layers for persistence (PNG dataURLs — canvas is the source). */
  function serializeLayers(): SketchLayerData[] {
    return serializeKind(sketchLayers.value);
  }

  /** Serialize ink layers for persistence (kept separate from sketch — Bug 4). */
  function serializeInkLayers(): SketchLayerData[] {
    return serializeKind(inkLayers.value);
  }

  /**
   * Render a small single-tile preview (visible sketch layers + ink) for the
   * gallery. Returns a JPEG dataURL, or null if rendering fails.
   */
  function renderThumbnail(size = 160): string | null {
    try {
      // Render through the shared tessellation renderer (same planLayerPlacement
      // decision as the live canvas and the wallpaper export) so the thumbnail
      // shows the pattern with correct wallpaper-group symmetry. Previously this
      // blitted the raw layer canvases at the identity placement, i.e. only the
      // fundamental domain with none of its symmetry copies — the "wrong
      // tessellation / bits of content missing" gallery bug.
      const tiles = renderWallpaperTiles(1, size);
      const c = document.createElement('canvas');
      c.width = tiles.width;
      c.height = tiles.height;
      const ctx = c.getContext('2d');
      if (!ctx) return null;
      const bg =
        getComputedStyle(document.documentElement).getPropertyValue('--tile-fill-active').trim() || '#1c1925';
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(tiles, 0, 0);
      return c.toDataURL('image/jpeg', 0.72);
    } catch {
      return null;
    }
  }

  // --- Trace (vector, unconstrained tile-space coords; one shape per layer) ---
  const traceLayers = ref<TraceLayerData[]>([]);
  /** id of the layer currently being drawn/edited (draft), or null */
  const editingTraceLayerId = ref<string | null>(null);
  const traceUndo = ref<TraceCommand[]>([]);
  const traceRedo = ref<TraceCommand[]>([]);

  const editingTraceLayer = computed(
    () => traceLayers.value.find((l) => l.id === editingTraceLayerId.value) ?? null,
  );

  function findTraceLayer(id: string): TraceLayerData | undefined {
    return traceLayers.value.find((l) => l.id === id);
  }

  /** Recompute a layer's cached `d` from its nodes. */
  function rebuildTraceD(layer: TraceLayerData): void {
    layer.d = buildTraceD(layer.nodes, layer.closed);
  }

  /**
   * Add a draft Trace layer (one being drawn) at the top of the stack and mark
   * it as the editing layer. Not yet pushed to undo — that happens on commit.
   */
  function startTraceLayer(layer: TraceLayerData): void {
    traceLayers.value.push(layer);
    editingTraceLayerId.value = layer.id;
    revision.value++;
  }

  /** Commit the draft layer to the stack (OK FAB) — becomes undoable. */
  function commitTraceLayer(): void {
    const layer = editingTraceLayer.value;
    editingTraceLayerId.value = null;
    if (!layer) return;
    traceUndo.value.push({ type: 'add', layer: cloneTraceLayer(layer) });
    if (traceUndo.value.length > TRACE_UNDO_DEPTH) traceUndo.value.shift();
    traceRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Discard the draft layer (Cancel FAB). */
  function cancelTraceLayer(): void {
    const id = editingTraceLayerId.value;
    editingTraceLayerId.value = null;
    if (!id) return;
    traceLayers.value = traceLayers.value.filter((l) => l.id !== id);
    revision.value++;
  }

  function deleteTraceLayer(id: string): void {
    const index = traceLayers.value.findIndex((l) => l.id === id);
    if (index === -1) return;
    const layer = cloneTraceLayer(traceLayers.value[index]!);
    traceLayers.value = traceLayers.value.filter((l) => l.id !== id);
    if (editingTraceLayerId.value === id) editingTraceLayerId.value = null;
    traceUndo.value.push({ type: 'remove', layer, index });
    if (traceUndo.value.length > TRACE_UNDO_DEPTH) traceUndo.value.shift();
    traceRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Clear a trace layer's path (all nodes) but keep the layer itself (Layers
   *  panel per-layer erase button). Undoable as a single step. */
  function clearTraceLayer(id: string): void {
    const index = traceLayers.value.findIndex((l) => l.id === id);
    if (index === -1) return;
    const current = traceLayers.value[index]!;
    if (current.nodes.length === 0) return;
    const before = cloneTraceLayer(current);
    const after = cloneTraceLayer(current);
    after.nodes = [];
    after.d = '';
    if (editingTraceLayerId.value === id) editingTraceLayerId.value = null;
    const next = [...traceLayers.value];
    next[index] = after;
    traceLayers.value = next;
    traceUndo.value.push({ type: 'clear', before, after: cloneTraceLayer(after) });
    if (traceUndo.value.length > TRACE_UNDO_DEPTH) traceUndo.value.shift();
    traceRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = true;
    revision.value++;
  }

  /** Reorder a trace layer (brief 09-layout: the Layers panel's up/down
   *  arrows) — same display-reversed convention and no-undo simplicity as
   *  `moveRasterLayer` above. */
  function moveTraceLayer(id: string, dir: 'up' | 'down'): void {
    const i = traceLayers.value.findIndex((l) => l.id === id);
    const j = dir === 'up' ? i + 1 : i - 1;
    if (i === -1 || j < 0 || j >= traceLayers.value.length) return;
    const next = [...traceLayers.value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    traceLayers.value = next;
    isDirty.value = true;
    revision.value++;
  }

  /**
   * Push a source handle's value onto every copy that links to it in that slot
   * (brief §4). No swap decision here — `sourceSlot` was decided and persisted at
   * link creation; propagation is a pure application of the stored matrix's
   * linear part. Rebuilds the source's own layer (its handle was just written by
   * the caller) plus every layer carrying a driven copy.
   */
  function propagateHandle(sourceLayerId: string, sourceNodeId: string, sourceSlot: 'in' | 'out'): void {
    const sourceLayer = findTraceLayer(sourceLayerId);
    const source = sourceLayer?.nodes.find((n) => n.id === sourceNodeId);
    if (!sourceLayer || !source) return;
    rebuildTraceD(sourceLayer);
    const hx = sourceSlot === 'in' ? source.inX : source.outX;
    const hy = sourceSlot === 'in' ? source.inY : source.outY;
    const refs = (link: TraceNode['inLink']): boolean =>
      !!link && link.sourceLayerId === sourceLayerId && link.sourceNodeId === sourceNodeId && link.sourceSlot === sourceSlot;
    for (const layer of traceLayers.value) {
      let touched = false;
      for (const node of layer.nodes) {
        if (refs(node.inLink)) {
          [node.inX, node.inY] = applyLinear(node.inLink!.matrix, hx, hy);
          touched = true;
        }
        if (refs(node.outLink)) {
          [node.outX, node.outY] = applyLinear(node.outLink!.matrix, hx, hy);
          touched = true;
        }
      }
      if (touched) rebuildTraceD(layer);
    }
  }

  /**
   * Edit a node in lockstep (brief §5). Callers send exactly one gesture's field
   * group at a time:
   *  - position (x,y) — walk the whole connected web (`positionWeb`) and move
   *    every member; position has no single root now that a corner links two
   *    sources, so there is no inverse-to-root step.
   *  - a handle (inX,inY or outX,outY) — if that handle is linked, invert the
   *    dragged vector onto the source's persisted slot and propagate from there;
   *    else it is a root handle, written directly and propagated to its copies.
   *  - type — only ever sent for a fully-unlinked node (Flip is disabled on any
   *    linked node — see canFlip), so it is applied straight to this node.
   */
  function applyNodeEdit(
    layerId: string,
    nodeId: string,
    desired: Partial<Pick<TraceNode, 'x' | 'y' | 'inX' | 'inY' | 'outX' | 'outY' | 'type'>>,
  ): void {
    const layer = findTraceLayer(layerId);
    const node = layer?.nodes.find((n) => n.id === nodeId);
    if (!layer || !node) return;

    // Position drag — move the whole web (positions only; handles are relative
    // offsets and stay consistent with every matrix fixed).
    if (desired.x !== undefined && desired.y !== undefined) {
      const touched = new Set<TraceLayerData>();
      for (const entry of positionWeb(traceLayers.value, layerId, nodeId)) {
        const l = findTraceLayer(entry.layerId);
        const nd = l?.nodes.find((n) => n.id === entry.nodeId);
        if (!l || !nd) continue;
        [nd.x, nd.y] = apply(entry.m, [desired.x, desired.y]);
        touched.add(l);
      }
      for (const l of touched) rebuildTraceD(l);
      isDirty.value = true;
      revision.value++;
      return;
    }

    // Push a dragged handle onto its source slot, through the link's inverse.
    const editHandle = (link: NonNullable<TraceNode['inLink']>, hx: number, hy: number): void => {
      const srcLayer = findTraceLayer(link.sourceLayerId);
      const srcNode = srcLayer?.nodes.find((n) => n.id === link.sourceNodeId);
      if (!srcLayer || !srcNode) return;
      const [vx, vy] = applyLinear(invert(link.matrix), hx, hy);
      if (link.sourceSlot === 'in') {
        srcNode.inX = vx;
        srcNode.inY = vy;
      } else {
        srcNode.outX = vx;
        srcNode.outY = vy;
      }
      propagateHandle(link.sourceLayerId, link.sourceNodeId, link.sourceSlot);
    };

    // type is only sent for a fully-unlinked node; it never affects `d`, so just
    // store it (any handle write below rebuilds; a lone type edit rebuilds at end).
    if (desired.type !== undefined) node.type = desired.type;

    if (desired.inX !== undefined && desired.inY !== undefined) {
      if (node.inLink) editHandle(node.inLink, desired.inX, desired.inY);
      else {
        node.inX = desired.inX;
        node.inY = desired.inY;
        propagateHandle(layerId, nodeId, 'in');
      }
    }
    if (desired.outX !== undefined && desired.outY !== undefined) {
      if (node.outLink) editHandle(node.outLink, desired.outX, desired.outY);
      else {
        node.outX = desired.outX;
        node.outY = desired.outY;
        propagateHandle(layerId, nodeId, 'out');
      }
    }
    if (desired.type !== undefined && desired.inX === undefined && desired.outX === undefined) {
      rebuildTraceD(layer);
    }

    isDirty.value = true;
    revision.value++;
  }

  function nextTraceName(): string {
    const maxN = traceLayers.value.reduce((n, l) => {
      const m = /(\d+)$/.exec(l.name);
      return Math.max(n, m ? Number(m[1]) : 0);
    }, 0);
    return `Trace ${maxN + 1}`;
  }

  function undoTrace(): void {
    const cmd = traceUndo.value.pop();
    if (!cmd) return;
    if (cmd.type === 'add') {
      traceLayers.value = traceLayers.value.filter((l) => l.id !== cmd.layer.id);
    } else if (cmd.type === 'clear') {
      traceLayers.value = traceLayers.value.map((l) => (l.id === cmd.before.id ? cloneTraceLayer(cmd.before) : l));
    } else {
      const idx = Math.min(cmd.index, traceLayers.value.length);
      traceLayers.value.splice(idx, 0, cloneTraceLayer(cmd.layer));
    }
    traceRedo.value.push(cmd);
    isDirty.value = true;
    revision.value++;
  }

  function redoTrace(): void {
    const cmd = traceRedo.value.pop();
    if (!cmd) return;
    if (cmd.type === 'add') {
      traceLayers.value.push(cloneTraceLayer(cmd.layer));
    } else if (cmd.type === 'clear') {
      traceLayers.value = traceLayers.value.map((l) => (l.id === cmd.after.id ? cloneTraceLayer(cmd.after) : l));
    } else {
      traceLayers.value = traceLayers.value.filter((l) => l.id !== cmd.layer.id);
    }
    traceUndo.value.push(cmd);
    isDirty.value = true;
    revision.value++;
  }

  /**
   * Serialize Trace layers for persistence (plain data — `d` is derived). The
   * uncommitted draft (if any) is excluded — only OK'd layers are saved.
   */
  function serializeTraceLayers(): TraceLayerData[] {
    return traceLayers.value.filter((l) => l.id !== editingTraceLayerId.value).map(cloneTraceLayer);
  }

  const isDirty = ref(false);

  async function loadContent(content: {
    reference: ReferenceState;
    sketchLayers: SketchLayerData[];
    inkLayers?: SketchLayerData[];
    traceLayers?: TraceLayerData[];
    /** legacy flattened paths — migrated to Trace layers when no `traceLayers` */
    paths?: InkPath[];
  }): Promise<void> {
    Object.assign(reference, content.reference, { editMode: false });
    canvasRegistry.clear();
    countRegistry.clear();
    imageDataRegistry.clear();
    instanceRegistry.clear();
    importRectRegistry.clear();
    inkGroupRegistry.clear();
    sketchClasses.clear();
    sketchGroupRegistry.clear();

    // Sketch layers (always ≥1). Legacy saves predate the sketch/ink split and
    // stored everything here; they load as sketch, which is the safe default.
    const sketchData = content.sketchLayers.length > 0 ? content.sketchLayers : [];
    if (sketchData.length === 0) {
      sketchLayers.value = [makeSketchLayer(1, 'sketch')];
    } else {
      sketchLayers.value = sketchData.map((d) => ({ id: d.id, name: d.name, opacity: d.opacity, visible: d.visible, kind: 'sketch' }));
      for (const d of sketchData) {
        await hydrateCanvas(d.id, getLayerCanvas(d.id), d);
        // Rebuild coloured-sketch extra class buffers (colour travels with the stroke).
        if (d.sketchClassPngs) {
          for (const { cls, png } of d.sketchClassPngs) {
            const canvas = await decodePng(png);
            if (!canvas || cls <= 0) continue;
            const cid = sketchCountId(d.id, cls);
            const target = getLayerCanvas(cid);
            target.getContext('2d')!.drawImage(canvas, 0, 0);
            // back-populate counts from alpha so the layer keeps building/erasing
            const px = canvas.getContext('2d')!.getImageData(0, 0, TILE_RASTER, TILE_RASTER).data;
            const counts = getSketchCounts(d.id, cls);
            const k = (isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light).k;
            for (let i = 0; i < counts.length; i++) counts[i] = alphaToCount(px[i * 4 + 3]! / 255, k);
          }
        }
        // Rebuild frozen colour paint groups (brief 10-colours — a later
        // colour change must never recolour these, same rule as ink).
        if (d.sketchColourGroups && d.sketchColourGroups.length > 0) {
          const groups: SketchGroup[] = [];
          for (const g of d.sketchColourGroups) {
            const classes = new Map<number, { counts: Float32Array; canvas: HTMLCanvasElement; img: ImageData }>();
            for (const { cls, png } of g.classes) {
              const canvas = await decodePng(png);
              if (!canvas) continue;
              const img = canvas.getContext('2d')!.createImageData(TILE_RASTER, TILE_RASTER);
              // back-populate counts from alpha so the group keeps building/erasing
              const px = canvas.getContext('2d')!.getImageData(0, 0, TILE_RASTER, TILE_RASTER).data;
              const counts = new Float32Array(TILE_RASTER * TILE_RASTER);
              const k = (isDarkMode() ? SKETCH_THEMES.dark : SKETCH_THEMES.light).k;
              for (let i = 0; i < counts.length; i++) counts[i] = alphaToCount(px[i * 4 + 3]! / 255, k);
              classes.set(cls, { counts, canvas, img });
            }
            groups.push({ paletteIndices: paletteIndicesFromLegacy(g), count: g.count, classes });
          }
          sketchGroupRegistry.set(d.id, groups);
        }
      }
    }
    activeSketchLayerId.value = sketchLayers.value[sketchLayers.value.length - 1]!.id;

    // Ink layers (may be empty — created on demand). Authored ink rebuilds its
    // paint groups; imported/legacy ink hydrates a single canvas.
    const inkData = content.inkLayers ?? [];
    inkLayers.value = inkData.map((d) => ({ id: d.id, name: d.name, opacity: d.opacity, visible: d.visible, kind: 'ink' }));
    for (const d of inkData) {
      if (d.inkGroups && d.inkGroups.length > 0) {
        const groups: InkGroup[] = [];
        for (const g of d.inkGroups) {
          const classes = new Map<number, HTMLCanvasElement>();
          for (const { cls, png } of g.classes) {
            const canvas = await decodePng(png);
            if (canvas) classes.set(cls, canvas);
          }
          groups.push({ paletteIndices: paletteIndicesFromLegacy(g), count: g.count, classes });
        }
        inkGroupRegistry.set(d.id, groups);
      } else {
        await hydrateInkCanvas(getLayerCanvas(d.id), d);
      }
    }
    activeInkLayerId.value = inkLayers.value[inkLayers.value.length - 1]?.id ?? null;

    traceLayers.value = content.traceLayers
      ? migrateTraceLayers(content.traceLayers).map(cloneTraceLayer)
      : (content.paths ?? []).map(inkPathToTraceLayer);
    editingTraceLayerId.value = null;
    rasterUndo.value = [];
    rasterRedo.value = [];
    traceUndo.value = [];
    traceRedo.value = [];
    clearUndo.value = null;
    clearRedo.value = null;
    isDirty.value = false;
    revision.value++;
  }

  function clearAll(): void {
    void loadContent({ reference: defaultReference(), sketchLayers: [], traceLayers: [] });
  }

  // One level of undo/redo for "clear everything" (brief: the delete-all
  // action must be undoable). Held outside the raster/trace stacks — clearing
  // wipes both of those anyway — and checked first by undo()/redo() regardless
  // of activeLayer, since the action spans both. Any subsequent edit forfeits
  // it, the same way a fresh commit already forfeits the normal redo stacks.
  const clearUndo = ref<ClearAllSnapshot | null>(null);
  const clearRedo = ref<ClearAllSnapshot | null>(null);

  /** Clear drawing (all sketch + ink + trace layers) but keep the reference image. */
  function clearDrawing(): void {
    clearUndo.value = markRaw(captureClearAllSnapshot());
    clearRedo.value = null;
    wipeDrawing();
  }

  /** The actual wipe, shared by the first clear and by redoing one. */
  function wipeDrawing(): void {
    canvasRegistry.clear();
    countRegistry.clear();
    imageDataRegistry.clear();
    instanceRegistry.clear();
    importRectRegistry.clear();
    inkGroupRegistry.clear();
    sketchClasses.clear();
    sketchGroupRegistry.clear();
    sketchLayers.value = [makeSketchLayer(1, 'sketch')];
    activeSketchLayerId.value = sketchLayers.value[0]!.id;
    inkLayers.value = [];
    activeInkLayerId.value = null;
    traceLayers.value = [];
    editingTraceLayerId.value = null;
    rasterUndo.value = [];
    rasterRedo.value = [];
    traceUndo.value = [];
    traceRedo.value = [];
    isDirty.value = true;
    revision.value++;
  }

  /** Restore one raster layer's content (counts / classes / groups / canvas)
   *  from a snapshot, replacing whatever is currently registered. Assumes the
   *  layer's existing extra classes/groups have already been wiped (callers
   *  either start from an empty layer or call `wipeLayerContent` first). */
  function applyLayerSnapshot(s: LayerSnapshot): void {
    const id = s.layer.id;
    if (s.layer.kind === 'sketch') {
      countRegistry.set(id, new Float32Array(s.counts));
      getLayerCanvas(id);
      renderCountsToCanvas(id);
      if (s.sketchClasses) {
        const set = new Set<number>();
        for (const [k, buf] of s.sketchClasses) {
          set.add(k);
          countRegistry.set(sketchCountId(id, k), new Float32Array(buf));
          getLayerCanvas(sketchCountId(id, k));
          renderCountsToCanvas(sketchCountId(id, k));
        }
        sketchClasses.set(id, set);
      }
      if (s.sketchGroups) restoreSketchGroups(id, s.sketchGroups);
    } else {
      canvasRegistry.set(id, copyCanvas(s.canvas));
    }
    if (s.inkGroups) restoreInkGroups(id, s.inkGroups);
  }

  /** Wipe every drawn-content registry for one raster layer while keeping its
   *  meta — the destructive half of the "Clear Layer" button (and the inverse
   *  of `applyLayerSnapshot`). Mirrors exactly the fields `captureLayerSnapshot`
   *  records, so capture → wipe → apply round-trips losslessly. */
  function wipeLayerContent(id: string): void {
    disposeSketchClasses(id);
    disposeSketchGroups(id);
    inkGroupRegistry.delete(id);
    const base = countRegistry.get(id);
    if (base) base.fill(0);
    const canvas = getLayerCanvas(id);
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, TILE_RASTER, TILE_RASTER);
    if (base) renderCountsToCanvas(id);
  }

  function captureClearAllSnapshot(): ClearAllSnapshot {
    return {
      sketchLayers: sketchLayers.value.map(captureLayerSnapshot),
      activeSketchLayerId: activeSketchLayerId.value,
      inkLayers: inkLayers.value.map(captureLayerSnapshot),
      activeInkLayerId: activeInkLayerId.value,
      traceLayers: traceLayers.value.map(cloneTraceLayer),
      editingTraceLayerId: editingTraceLayerId.value,
    };
  }

  /** Rebuild every layer/registry from a clear-all snapshot. Always makes
   *  fresh copies (via restoreInkGroups / copyCanvas / cloned Float32Arrays) so
   *  repeated undo↔redo cycles can't corrupt the retained snapshot. */
  function restoreClearAllSnapshot(snap: ClearAllSnapshot): void {
    const restoreOne = (s: LayerSnapshot): void => applyLayerSnapshot(s);

    sketchLayers.value = snap.sketchLayers.map((s) => ({ ...s.layer }));
    inkLayers.value = snap.inkLayers.map((s) => ({ ...s.layer }));
    snap.sketchLayers.forEach(restoreOne);
    snap.inkLayers.forEach(restoreOne);
    activeSketchLayerId.value = snap.activeSketchLayerId;
    activeInkLayerId.value = snap.activeInkLayerId;
    traceLayers.value = snap.traceLayers.map(cloneTraceLayer);
    editingTraceLayerId.value = snap.editingTraceLayerId;
    rasterUndo.value = [];
    rasterRedo.value = [];
    traceUndo.value = [];
    traceRedo.value = [];
    isDirty.value = true;
    revision.value++;
  }

  /**
   * Everything Render PSD needs to snapshot/restore around a transient render
   * (brief 03-procreate.md "Rendering") — unlike clearDrawing's undoable-clear
   * snapshot, this must round-trip the undo/redo history and the dirty flag
   * byte-for-byte, since a render must be completely invisible to the user's
   * actual session.
   */
  function captureRenderState() {
    return {
      content: captureClearAllSnapshot(),
      rasterUndo: rasterUndo.value.slice(),
      rasterRedo: rasterRedo.value.slice(),
      traceUndo: traceUndo.value.slice(),
      traceRedo: traceRedo.value.slice(),
      clearUndoSlot: clearUndo.value,
      clearRedoSlot: clearRedo.value,
      activeLayer: activeLayer.value,
      isDirty: isDirty.value,
    };
  }

  /** Rebuild every layer/registry from a render-state snapshot, then restore
   *  everything `restoreClearAllSnapshot` clobbers (it resets undo/redo/dirty
   *  for its own — correct, for an undoable clear — purposes). */
  function restoreRenderState(snap: ReturnType<typeof captureRenderState>): void {
    restoreClearAllSnapshot(snap.content);
    rasterUndo.value = snap.rasterUndo;
    rasterRedo.value = snap.rasterRedo;
    traceUndo.value = snap.traceUndo;
    traceRedo.value = snap.traceRedo;
    clearUndo.value = snap.clearUndoSlot;
    clearRedo.value = snap.clearRedoSlot;
    activeLayer.value = snap.activeLayer;
    isDirty.value = snap.isDirty;
  }

  /** Wipe every layer/registry for a transient render, without touching the
   *  undoable-clear slot (the caller holds its own snapshot via
   *  `captureRenderState`, restored via `restoreRenderState`). */
  function replaceForRender(): void {
    wipeDrawing();
  }

  const canUndo = computed(
    () =>
      !!clearUndo.value ||
      (activeLayer.value === 'trace' ? traceUndo.value.length > 0 : rasterUndo.value.length > 0),
  );
  const canRedo = computed(
    () =>
      !!clearRedo.value ||
      (activeLayer.value === 'trace' ? traceRedo.value.length > 0 : rasterRedo.value.length > 0),
  );

  /** Undo/redo dispatched to the active layer's history (toolbar + keyboard).
   *  A pending clear-all takes priority — it spans both raster and trace, so
   *  it can't be scoped to just one of those two stacks. */
  function undo(): void {
    if (clearUndo.value) {
      restoreClearAllSnapshot(clearUndo.value);
      clearRedo.value = clearUndo.value;
      clearUndo.value = null;
      return;
    }
    if (activeLayer.value === 'trace') undoTrace();
    else undoSketch();
  }
  function redo(): void {
    if (clearRedo.value) {
      wipeDrawing();
      clearUndo.value = clearRedo.value;
      clearRedo.value = null;
      return;
    }
    if (activeLayer.value === 'trace') redoTrace();
    else redoSketch();
  }

  function rerenderAllSketchLayers(): void {
    for (const id of countRegistry.keys()) renderCountsToCanvas(id);
    revision.value++;
  }

  return {
    activeLayer,
    revision,
    reference,
    sketchLayers,
    inkLayers,
    rasterLayers,
    activeSketchLayerId,
    activeInkLayerId,
    activeSketchLayer,
    activeRasterLayer,
    getLayerCanvas,
    getLayerInstances,
    getLayerImportRect,
    getLayerCounts,
    getLayerKind,
    getInkDrawTarget,
    getInkGroups,
    hasInkGroups,
    eachInkCanvas,
    snapshotInkGroups,
    restoreInkGroups,
    commitInk,
    compositeInkLayer,
    compositeSketchGroups,
    hasCountBuffer,
    getSketchCounts,
    sketchCountId,
    sketchClassList,
    sketchClassCanvas,
    snapshotSketch,
    restoreSketch,
    commitSketch,
    getSketchGroupCounts,
    getSketchGroups,
    hasSketchGroups,
    eachSketchGroupCounts,
    snapshotSketchGroups,
    restoreSketchGroups,
    commitSketchGroups,
    snapshotCounts,
    snapshotLayer,
    commitRaster,
    commitRasterCanvas,
    undoSketch,
    redoSketch,
    addSketchLayer,
    addInkLayer,
    ensureLayerForKind,
    setMode,
    selectRasterLayer,
    addImportedRasterLayer,
    addImportedRasterLayerSet,
    removeSketchLayer,
    removeRasterLayer,
    clearRasterLayer,
    moveRasterLayer,
    serializeLayers,
    serializeInkLayers,
    renderThumbnail,
    traceLayers,
    editingTraceLayerId,
    editingTraceLayer,
    findTraceLayer,
    rebuildTraceD,
    startTraceLayer,
    beginEditingTraceLayer,
    commitTraceLayer,
    cancelTraceLayer,
    deleteTraceLayer,
    clearTraceLayer,
    moveTraceLayer,
    applyNodeEdit,
    nextTraceName,
    serializeTraceLayers,
    undoTrace,
    redoTrace,
    isDirty,
    loadContent,
    clearAll,
    clearDrawing,
    canUndo,
    canRedo,
    undo,
    redo,
    captureRenderState,
    restoreRenderState,
    replaceForRender,
    rerenderAllSketchLayers,
  };
});

// ---------------------------------------------------------------------------
// Module-level helpers (used by both the store and useDrawing)
// ---------------------------------------------------------------------------

function getOrCreateCounts(id: string): Float32Array {
  let buf = countRegistry.get(id);
  if (!buf) {
    buf = new Float32Array(TILE_RASTER * TILE_RASTER);
    countRegistry.set(id, buf);
  }
  return buf;
}